/**
 * Revision history viewer — read-only snapshot log for a dashboard
 * (populated by `reporting.signals.on_dashboard_saved` / `on_indicator_saved`).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Loader2 } from 'lucide-react';
import {
  reportingAPI,
  type AnalyseDashboard,
  type ReportRevisionEntry,
} from '../../../lib/api/reporting';

interface RevisionHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: AnalyseDashboard | null;
}

export function RevisionHistoryDialog({ open, onOpenChange, report }: RevisionHistoryDialogProps) {
  const [loading, setLoading] = useState(false);
  const [revisions, setRevisions] = useState<ReportRevisionEntry[]>([]);
  const [selected, setSelected] = useState<ReportRevisionEntry | null>(null);

  const load = useCallback(async (dashboardId: number) => {
    setLoading(true);
    try {
      const response = await reportingAPI.listRevisions({ dashboard: dashboardId });
      setRevisions(response.data.results || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!report || !open) return;
    void load(report.id);
    setSelected(null);
  }, [report, open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Revision history — {report?.name}</DialogTitle>
          <DialogDescription>
            Every save of this dashboard (or any of its indicators) is recorded below.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 grid grid-cols-[320px_1fr] gap-4 overflow-hidden">
          {/* Timeline */}
          <div className="border rounded-md overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading…
              </div>
            ) : revisions.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">No revisions yet.</div>
            ) : (
              revisions.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelected(r)}
                  className={`w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-muted ${
                    selected?.id === r.id ? 'bg-muted' : ''
                  }`}
                >
                  <div className="text-xs font-medium">{r.note || '(update)'}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {new Date(r.changed_at).toLocaleString()} ·{' '}
                    {r.changed_by_name || 'system'}
                    {r.indicator && ` · indicator #${r.indicator}`}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Snapshot */}
          <div className="border rounded-md p-3 overflow-y-auto">
            {!selected ? (
              <div className="text-sm text-muted-foreground text-center py-12">
                Pick a revision to see its snapshot.
              </div>
            ) : (
              <pre className="text-xs whitespace-pre-wrap font-mono">
                {JSON.stringify(selected.config_snapshot, null, 2)}
              </pre>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
